import { dailyNumber, dailyOf, dayOf } from "@obean/game-contract";
import { useSignal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import games from "virtual:obean-games";
import { profileId } from "../loop/profile.js";
import { api } from "../net/api.js";
import { navigate } from "../router.js";
import { rememberInvite } from "../pages/invite-memory.js";
import { dailyBest } from "../solo/daily.js";
import { loadProgress, soloMissions } from "../solo/progress.js";
import { carryOn, recallLastPlay } from "./home.js";

type Friend = Awaited<ReturnType<typeof api.friends>>["friends"][number];

/**
 * The start page for someone who has played here before (P5-UX-04): carry on in the game played
 * last, in one tap; today's daily in every game, with what's done; and friends online to play
 * with. A first visit sees none of it, only making a room or trying a game.
 */
export function WelcomeBack() {
  const last = recallLastPlay();
  const profile = useSignal<string | null>(null);
  const friends = useSignal<Friend[]>([]);
  const busy = useSignal<string | null>(null);
  useEffect(() => {
    if (!last) return;
    void profileId().then((p) => {
      profile.value = p;
    });
    void api.friends().then(
      (f) => {
        friends.value = f.friends.filter((x) => x.online === true);
      },
      () => undefined, // not signed in, or nobody yet
    );
  }, []);
  if (!last) return null;

  const day = dayOf(Date.now());
  const live = games.filter((g) => g.stage === "live");
  const lastGame = live.find((g) => g.id === last.gameId);
  const daily = lastGame ? dailyOf(lastGame, day) : null;
  const next = lastGame
    ? carryOn(
        soloMissions(lastGame.missions, loadProgress(lastGame.id, undefined, profile.value), []),
        daily ? { missionId: daily.missionId, played: dailyBest(lastGame.id, day) !== null } : null,
      )
    : null;
  const dailies = live.flatMap((g) => {
    const d = dailyOf(g, day);
    return d ? [{ game: g, done: dailyBest(g.id, day) !== null }] : [];
  });
  const playWith = async (f: Friend) => {
    busy.value = f.id;
    try {
      const { roomId, invite } = await api.createRoom();
      rememberInvite(roomId, invite);
      await api.inviteFriend(f.id, roomId).catch(() => undefined);
      navigate(`/r/${roomId}`);
    } catch {
      busy.value = null;
      navigate("/new");
    }
  };

  return (
    <div class="card welcome-back" role="group" aria-label="Welcome back">
      {lastGame && next && (
        <a
          class="button carry-on"
          href={
            next.kind === "daily"
              ? `/solo?game=${encodeURIComponent(lastGame.id)}&daily=1`
              : `/solo?game=${encodeURIComponent(lastGame.id)}&mission=${encodeURIComponent(next.missionId)}`
          }
        >
          Carry on: {lastGame.displayName} ·{" "}
          {next.kind === "daily" ? `Today's daily #${String(dailyNumber(day))}` : next.name}
        </a>
      )}
      {friends.value.length > 0 && (
        <ul class="friends-online" aria-label="Friends online">
          {friends.value.map((f) => (
            <li key={f.id}>
              <span>
                <b>{f.name}</b> is online
                {f.game ? ` in ${games.find((g) => g.id === f.game)?.displayName ?? "a game"}` : ""}
              </span>
              <button
                type="button"
                class="secondary"
                disabled={busy.value !== null}
                onClick={() => void playWith(f)}
              >
                Play together
              </button>
            </li>
          ))}
        </ul>
      )}
      {dailies.length > 0 && (
        <div class="dailies">
          <p class="hint">Today's daily #{dailyNumber(day)}, the same for everyone:</p>
          <ul aria-label="Today's dailies">
            {dailies.map(({ game, done }) => (
              <li key={game.id}>
                {done ? (
                  <span class="done">
                    <span aria-hidden="true">✓ </span>
                    {game.displayName}
                    <span class="visually-hidden"> (played)</span>
                  </span>
                ) : (
                  <a href={`/solo?game=${encodeURIComponent(game.id)}&daily=1`}>
                    {game.displayName}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
